/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    cursor,
    replyState,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        cursor,
        reply_state: replyState,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
        cursor: payload.cursor,
      },
    });
  }

  needsReplyCount({ filters = {}, queryData = null, force = false }) {
    const body = queryData || {
      inbox_id: filters.inboxId,
      status: filters.status,
      labels: filters.labels,
      team_id: filters.teamId,
      conversation_type: filters.conversationType,
    };
    return axios.post(`${this.url}/needs_reply_count`, body, {
      params: { force },
    });
  }

  // MakerHub: bulk-clear the Urgent tab. Accepts older_than_days / read filters.
  clearUrgent(params = {}) {
    return axios.post(`${this.url}/clear_urgent`, params);
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  // MakerHub: single-conversation "Resolve" → mark handled (merge-safe server-side
  // jsonb_set; keeps status open so the conversation stays in the All tab).
  markHandled(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mark_handled`);
  }

  // MakerHub: WhatsApp group roster (proxied from Evolution) for the reply-box
  // @-mention picker.
  getGroupParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/group_participants`);
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  // MakerHub: explicit "pin to Pending tab" override (custom_attributes.pin_pending);
  // the finder id-unions it into the Pending tab alongside the computed queue.
  togglePending({ conversationId, pending }) {
    return axios.post(`${this.url}/${conversationId}/toggle_pending`, {
      pending,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId, page) {
    const url = `${this.url}/${conversationId}/attachments`;
    return page ? axios.get(url, { params: { page } }) : axios.get(url);
  }

  getTranscript(conversationId, includeNotes) {
    return axios.get(`${this.url}/${conversationId}/mh_transcript`, {
      params: includeNotes ? { notes: 1 } : undefined,
    });
  }

  getLinks(conversationId) {
    return axios.get(`${this.url}/${conversationId}/mh_links`);
  }

  getS3Panel(conversationId) {
    return axios.get(`${this.url}/${conversationId}/mh_s3_panel`);
  }

  searchBukkuContacts(conversationId, q) {
    return axios.get(
      `${this.url}/${conversationId}/mh_s3_panel/bukku_contacts`,
      { params: { q } }
    );
  }

  linkBukkuContact(conversationId, bukkuContactId) {
    return axios.post(`${this.url}/${conversationId}/mh_s3_panel/bukku_link`, {
      bukku_contact_id: bukkuContactId,
    });
  }

  getS3EmbedTicket(conversationId, url) {
    return axios.post(
      `${this.url}/${conversationId}/mh_s3_panel/embed_ticket`,
      { url }
    );
  }

  unlinkBukkuContact(conversationId) {
    return axios.delete(`${this.url}/${conversationId}/mh_s3_panel/bukku_link`);
  }

  toggleMessagePin({ conversationId, messageId, pinned }) {
    return axios.post(`${this.url}/${conversationId}/mh_pin_message`, {
      message_id: messageId,
      pinned,
    });
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
